import React, { useState, Suspense, useEffect } from 'react';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import Advisor from './pages/Advisor';
import { ForecastPage, FaultsPage, ZonesPage } from './pages/OtherPages';
import IndiaMap from './pages/IndiaMap';
import { useGridData } from './hooks/useGridData';
import About from './pages/About';
import CertificateRegistry from "./pages/CertificateRegistry";


const TransformerPage = React.lazy(() => import('./pages/TransformerPage'));
const FrequencyPage = React.lazy(() => import('./pages/FrequencyPage'));
const LowVoltagePage = React.lazy(() => import('./pages/LowVoltagePage'));
const VillageGridPage = React.lazy(() => import('./pages/VillageGridPage'));
const CarbonIntelligence = React.lazy(() =>
  import('./pages/CarbonIntelligence')
);

function Loading() {
  return (
    <div
      style={{
        padding: 40,
        color: '#00dc64',
        fontFamily: 'IBM Plex Mono, monospace',
        fontSize: 13,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
      }}
    >
      <div
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: '#00dc64',
          animation: 'pulse 1s infinite',
        }}
      />
      Loading...
    </div>
  );
}

const PAGES = {
  dashboard: <Dashboard />,
  certificates: <CertificateRegistry />,
  forecast: <ForecastPage />,
  faults: <FaultsPage />,
  advisor: <Advisor />,
  zones: <ZonesPage />,
  map: <IndiaMap />,

  transformer: (
    <Suspense fallback={<Loading />}>
      <TransformerPage />
    </Suspense>
  ),

  frequency: (
    <Suspense fallback={<Loading />}>
      <FrequencyPage />
    </Suspense>
  ),

  lowvoltage: (
    <Suspense fallback={<Loading />}>
      <LowVoltagePage />
    </Suspense>
  ),

  villagegrid: (
    <Suspense fallback={<Loading />}>
      <VillageGridPage />
    </Suspense>
  ),

  carbon: (
    <Suspense fallback={<Loading />}>
      <CarbonIntelligence />
    </Suspense>
  ),

  about: <About />,
};

/*
  SE1 — this app is now mounted at /gridmind (www.intellilearnai.com/ is a
  separate static company page; see vercel.json). BASE_PATH is stripped
  before path matching so the dashboard's own in-app routing (About) keeps
  working the same way it did at root, just one level down.

  /gridmind         -> Dashboard
  /gridmind/about   -> About page (superseded externally by the
                        /about -> /founder redirect in vercel.json; this
                        in-app path only matters for the Layout nav item
                        that still links here from inside the running app)

  All other operational modules continue working through
  the existing activePage state without changing their UI.
*/
const BASE_PATH = '/gridmind';

function getPageFromPath() {
  let path = window.location.pathname
    .replace(/\/+$/, '')
    .toLowerCase();

  if (path.startsWith(BASE_PATH)) {
    path = path.slice(BASE_PATH.length) || '/';
  }

  if (path === '/about') {
    return 'about';
  }

  return 'dashboard';
}

export default function App() {
  const [activePage, setActivePage] = useState(getPageFromPath);

  const { gridData, source } = useGridData(5000);

  const handleNavigate = (pageId) => {
    setActivePage(pageId);

    if (pageId === 'about') {
      if (window.location.pathname !== `${BASE_PATH}/about`) {
        window.history.pushState({}, '', `${BASE_PATH}/about`);
      }
    } else {
      /*
        Return to the app's base URL for operational dashboard modules.

        We use replaceState here so clicking internal modules
        doesn't fill browser history with duplicate root entries.
      */
      if (window.location.pathname !== BASE_PATH) {
        window.history.pushState({}, '', BASE_PATH);
      }
    }

    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    const handlePopState = () => {
      setActivePage(getPageFromPath());
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  useEffect(() => {
    if (activePage === 'about') {
      document.title =
        'About GridMind, IntelliLearn AI & Founder Shivam Chaudhary';
    } else {
      document.title =
        'GridMind — Grid Intelligence by IntelliLearn AI';
    }
  }, [activePage]);

  return (
    <div className="app-layout">
      <Layout
        activePage={activePage}
        onNavigate={handleNavigate}
        gridData={gridData}
        source={source}
      />

      <main className="main-content">
        {PAGES[activePage] || <Dashboard />}
      </main>
    </div>
  );
}